import React, { Fragment, useEffect, useRef, useState } from 'react';
import GraduateVideoPlayer from './GraduateVideoPlayer';
import {
  GRADUATE_VIDEOS,
  REVIEWS_TITLE,
  TAU_EXPERIENCE_HIGHLIGHTS,
  TAU_EXPERIENCE_TEXT,
  VIDEOS_TITLE,
  VIDEOS_TITLE_LINES,
  WRITTEN_REVIEWS,
} from '../content/studentRecommendations';

function getResponsiveVisibleCount(requested) {
  if (typeof window === 'undefined') return requested;
  const width = window.innerWidth;
  if (width <= 640) return 1;
  if (width <= 960) return Math.min(requested, 2);
  return Math.max(1, requested);
}

function useResponsiveVisibleCount(requested) {
  const [count, setCount] = useState(() => getResponsiveVisibleCount(requested));
  useEffect(() => {
    const apply = () => setCount(getResponsiveVisibleCount(requested));
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [requested]);
  return count;
}

function indexOfNearestChild(track, align) {
  const kids = track.children;
  if (!kids.length) return 0;
  const target = align === 'start'
    ? track.scrollLeft
    : track.scrollLeft + track.clientWidth / 2;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < kids.length; i += 1) {
    const child = kids[i];
    const point = align === 'start'
      ? child.offsetLeft
      : child.offsetLeft + child.offsetWidth / 2;
    const dist = Math.abs(point - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

function SnapCarousel({
  items,
  renderItem,
  ariaPrev,
  ariaNext,
  className,
  visibleCount: requestedVisibleCount = 1,
  align = 'center',
}) {
  const visibleCount = useResponsiveVisibleCount(requestedVisibleCount);
  const trackRef = useRef(null);
  const jumpTimerRef = useRef(null);
  const programmaticRef = useRef(false);
  const [active, setActive] = useState(0);
  const step = Math.max(1, visibleCount);
  const loop = items.length > 1 && step === 1;

  const maxStart = () => Math.max(0, items.length - (step > 1 ? step : 1));

  const markProgrammatic = (ms = 520) => {
    programmaticRef.current = true;
    window.clearTimeout(jumpTimerRef.current);
    jumpTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false;
    }, ms);
  };

  const scrollToChild = (childIndex, behavior = 'smooth') => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[childIndex];
    if (!card) return;
    const left = align === 'start'
      ? card.offsetLeft
      : card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
    track.scrollTo({ left, behavior });
  };

  const afterScroll = (fn) => {
    const track = trackRef.current;
    if (!track) {
      fn();
      return;
    }
    window.clearTimeout(jumpTimerRef.current);
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      track.removeEventListener('scrollend', done);
      window.clearTimeout(jumpTimerRef.current);
      programmaticRef.current = false;
      fn();
    };
    track.addEventListener('scrollend', done);
    jumpTimerRef.current = window.setTimeout(done, 480);
  };

  const scrollToStart = (startIndex, behavior = 'smooth') => {
    const track = trackRef.current;
    if (!track || !items.length) return;
    const last = maxStart();
    markProgrammatic();

    if (loop) {
      if (startIndex > last) {
        setActive(0);
        scrollToChild(items.length + 1, behavior);
        afterScroll(() => scrollToChild(1, 'auto'));
        return;
      }
      if (startIndex < 0) {
        setActive(last);
        scrollToChild(0, behavior);
        afterScroll(() => scrollToChild(items.length, 'auto'));
        return;
      }
      const next = Math.min(Math.max(0, startIndex), last);
      scrollToChild(next + 1, behavior);
      setActive(next);
      return;
    }

    let next = startIndex;
    if (next > last) next = 0;
    else if (next < 0) next = last;
    next = Math.min(Math.max(0, next), last);
    scrollToChild(next, behavior);
    setActive(next);
  };

  const pageStartFor = (index) => (
    step > 1 ? Math.min(Math.floor(index / step) * step, maxStart()) : index
  );

  useEffect(() => {
    scrollToStart(0, 'auto');
    return () => window.clearTimeout(jumpTimerRef.current);
  }, [items.length, step]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !items.length) return undefined;
    let settleTimer;

    const syncFromScroll = (finalize) => {
      if (programmaticRef.current) return;
      const displayIndex = indexOfNearestChild(track, align);
      if (loop) {
        const last = Math.max(0, items.length - 1);
        if (displayIndex <= 0) {
          setActive(last);
          if (finalize) {
            programmaticRef.current = true;
            scrollToChild(items.length, 'auto');
            window.setTimeout(() => { programmaticRef.current = false; }, 80);
          }
          return;
        }
        if (displayIndex >= items.length + 1) {
          setActive(0);
          if (finalize) {
            programmaticRef.current = true;
            scrollToChild(1, 'auto');
            window.setTimeout(() => { programmaticRef.current = false; }, 80);
          }
          return;
        }
        setActive(displayIndex - 1);
        return;
      }
      setActive(pageStartFor(displayIndex));
    };

    const onScroll = () => {
      syncFromScroll(false);
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => syncFromScroll(true), 90);
    };
    const onScrollEnd = () => {
      window.clearTimeout(settleTimer);
      syncFromScroll(true);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('scrollend', onScrollEnd);
    return () => {
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('scrollend', onScrollEnd);
      window.clearTimeout(settleTimer);
    };
  }, [items.length, loop, step, align]);

  const slots = loop
    ? [
        { item: items[items.length - 1], originalIndex: items.length - 1, clone: true },
        ...items.map((item, originalIndex) => ({ item, originalIndex, clone: false })),
        { item: items[0], originalIndex: 0, clone: true },
      ]
    : items.map((item, originalIndex) => ({ item, originalIndex, clone: false }));

  return (
    <div className={className}>
      <div className="ceo-carousel">
        <button
          type="button"
          className="ceo-carousel-nav ceo-carousel-nav--prev"
          onClick={() => scrollToStart(active - step)}
          aria-label={ariaPrev}
        >
          ‹
        </button>
        <div className="ceo-carousel-track" ref={trackRef}>
          {slots.map((slot, displayIndex) => (
            <Fragment key={`snap-${displayIndex}`}>
              {renderItem(
                slot.item,
                slot.originalIndex,
                step > 1
                  ? slot.originalIndex >= active && slot.originalIndex < active + step
                  : slot.originalIndex === active,
                () => scrollToStart(pageStartFor(slot.originalIndex)),
              )}
            </Fragment>
          ))}
        </div>
        <button
          type="button"
          className="ceo-carousel-nav ceo-carousel-nav--next"
          onClick={() => scrollToStart(active + step)}
          aria-label={ariaNext}
        >
          ›
        </button>
      </div>
    </div>
  );
}

export default function StudentRecommendations({ tauLogo, collerLogo }) {
  const videoCarouselRef = useRef(null);
  const videoViewportRef = useRef(null);
  const videoDragRef = useRef({
    active: false,
    startX: 0,
    startScroll: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
  });
  const videoLoopLockRef = useRef(false);
  const [videoTrackIndex, setVideoTrackIndex] = useState(0);
  const [isVideoDragging, setIsVideoDragging] = useState(false);
  const loopedGraduateVideos = [...GRADUATE_VIDEOS, ...GRADUATE_VIDEOS, ...GRADUATE_VIDEOS];

  const pauseCarouselVideos = () => {
    videoCarouselRef.current?.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  };

  const getVideoSlideMetrics = () => {
    const viewport = videoViewportRef.current;
    if (!viewport) return { slideWidth: 0, peek: 0 };
    const slide = viewport.querySelector('.video-carousel-slide');
    const slideWidth = slide ? slide.offsetWidth : viewport.clientWidth * 0.58;
    const peek = Math.max(0, (viewport.clientWidth - slideWidth) / 2);
    return { slideWidth, peek };
  };

  const scrollVideoToIndex = (index, behavior = 'smooth') => {
    const viewport = videoViewportRef.current;
    if (!viewport) return;
    const { slideWidth, peek } = getVideoSlideMetrics();
    viewport.scrollTo({
      left: index * slideWidth - peek,
      behavior,
    });
  };

  const normalizeVideoLoop = () => {
    const viewport = videoViewportRef.current;
    if (!viewport || videoLoopLockRef.current) return;
    const total = GRADUATE_VIDEOS.length;
    const { slideWidth, peek } = getVideoSlideMetrics();
    if (!slideWidth) return;
    const index = Math.round((viewport.scrollLeft + peek) / slideWidth);
    if (index < total) {
      videoLoopLockRef.current = true;
      viewport.scrollLeft += total * slideWidth;
      videoLoopLockRef.current = false;
    } else if (index >= total * 2) {
      videoLoopLockRef.current = true;
      viewport.scrollLeft -= total * slideWidth;
      videoLoopLockRef.current = false;
    }
  };

  const goToVideoSlide = (index) => {
    pauseCarouselVideos();
    scrollVideoToIndex(index, 'smooth');
  };

  const handleVideoScroll = () => {
    const viewport = videoViewportRef.current;
    if (!viewport || videoLoopLockRef.current) return;
    const { slideWidth, peek } = getVideoSlideMetrics();
    if (!slideWidth) return;
    const index = Math.round((viewport.scrollLeft + peek) / slideWidth);
    setVideoTrackIndex(index);
    window.clearTimeout(viewport._loopTimer);
    viewport._loopTimer = window.setTimeout(() => {
      if (!videoDragRef.current.active) normalizeVideoLoop();
    }, 140);
  };

  useEffect(() => {
    if (GRADUATE_VIDEOS.length) {
      setVideoTrackIndex(GRADUATE_VIDEOS.length);
    }
  }, []);

  useEffect(() => {
    const viewport = videoViewportRef.current;
    if (!viewport || !GRADUATE_VIDEOS.length) return undefined;
    const { slideWidth, peek } = getVideoSlideMetrics();
    viewport.scrollLeft = GRADUATE_VIDEOS.length * slideWidth - peek;

    const onScrollEnd = () => normalizeVideoLoop();
    viewport.addEventListener('scrollend', onScrollEnd);

    const onWheel = (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      viewport.scrollLeft += event.deltaY;
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      viewport.removeEventListener('scrollend', onScrollEnd);
      viewport.removeEventListener('wheel', onWheel);
    };
  }, []);

  const handleVideoPointerDown = (event) => {
    if (event.target.closest('video, button, .story-player')) return;
    if (event.pointerType === 'touch') return;
    const viewport = videoViewportRef.current;
    if (!viewport) return;
    videoDragRef.current = {
      active: true,
      startX: event.clientX,
      startScroll: viewport.scrollLeft,
      lastX: event.clientX,
      lastTime: Date.now(),
      velocity: 0,
    };
    setIsVideoDragging(true);
    pauseCarouselVideos();
    viewport.setPointerCapture(event.pointerId);
  };

  const handleVideoPointerMove = (event) => {
    const drag = videoDragRef.current;
    const viewport = videoViewportRef.current;
    if (!drag.active || !viewport) return;
    const now = Date.now();
    const dt = Math.max(now - drag.lastTime, 1);
    drag.velocity = (event.clientX - drag.lastX) / dt;
    drag.lastX = event.clientX;
    drag.lastTime = now;
    viewport.scrollLeft = drag.startScroll - (event.clientX - drag.startX);
  };

  const handleVideoPointerUp = () => {
    const drag = videoDragRef.current;
    if (!drag.active) return;
    drag.active = false;
    setIsVideoDragging(false);
    const { slideWidth, peek } = getVideoSlideMetrics();
    const viewport = videoViewportRef.current;
    if (!viewport || !slideWidth) return;
    let index = Math.round((viewport.scrollLeft + peek) / slideWidth);
    if (drag.velocity < -0.35) index += 1;
    if (drag.velocity > 0.35) index -= 1;
    scrollVideoToIndex(index, 'smooth');
  };

  return (
    <>
      <div className="gl-stories-block" dir="rtl">
        <article className="gl-stories-trust">
          <div className="gl-stories-trust__sheen" aria-hidden="true" />
          <div className="gl-stories-trust__logos" aria-label="שותפים אקדמיים">
            {tauLogo && (
              <div className="gl-stories-trust__logo-chip">
                <img src={tauLogo} alt="אוניברסיטת תל אביב" className="gl-stories-trust__logo gl-stories-trust__logo--tau" />
              </div>
            )}
            {collerLogo && (
              <div className="gl-stories-trust__logo-chip">
                <img src={collerLogo} alt="פקולטה לניהול — אוניברסיטת תל אביב" className="gl-stories-trust__logo gl-stories-trust__logo--coller" />
              </div>
            )}
          </div>
          <p className="gl-stories-trust__text">{TAU_EXPERIENCE_TEXT}</p>
          <div className="gl-stories-trust__metrics" role="list">
            {TAU_EXPERIENCE_HIGHLIGHTS.map((item) => (
              <div key={item.id} className="gl-stories-trust__metric" role="listitem">
                <span className="gl-stories-trust__metric-value">{item.value}</span>
                <span className="gl-stories-trust__metric-label">{item.label}</span>
              </div>
            ))}
          </div>
        </article>

        <div className="video-testimonials-section gl-stories-videos">
          <header className="gl-stories-videos__head">
            <h3 className="gl-stories-videos__title" aria-label={VIDEOS_TITLE}>
              <span className="gl-stories-videos__title-mark" aria-hidden="true" />
              <span className="gl-stories-videos__title-lines" aria-hidden="true">
                <span className="gl-stories-videos__title-line">{VIDEOS_TITLE_LINES[0]}</span>
                <span className="gl-stories-videos__title-line gl-stories-videos__title-line--accent">
                  {VIDEOS_TITLE_LINES[1]}
                </span>
              </span>
            </h3>
          </header>

          <div className="video-carousel graduates-carousel gl-stories-carousel" ref={videoCarouselRef}>
          <button
            type="button"
            className="video-carousel-nav video-carousel-nav--prev graduates-carousel-nav"
            onClick={() => goToVideoSlide(videoTrackIndex - 1)}
            aria-label="המלצה קודמת"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14.5 5.5 9 12l5.5 6.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div
            ref={videoViewportRef}
            className={`video-carousel-viewport graduates-carousel-viewport${isVideoDragging ? ' is-dragging' : ''}`}
            onScroll={handleVideoScroll}
            onPointerDown={handleVideoPointerDown}
            onPointerMove={handleVideoPointerMove}
            onPointerUp={handleVideoPointerUp}
            onPointerCancel={handleVideoPointerUp}
          >
            <div className="video-carousel-track graduates-carousel-track">
              {loopedGraduateVideos.map((graduate, index) => {
                const total = GRADUATE_VIDEOS.length;
                const realIndex = index % total;
                const activeIndex = ((videoTrackIndex % total) + total) % total;
                const isPeek = realIndex !== activeIndex;
                const isCenter = index === videoTrackIndex;
                return (
                  <div
                    className={[
                      'video-carousel-slide',
                      'graduates-carousel-slide',
                      isPeek ? 'is-peek' : 'is-focus',
                      isCenter ? 'is-center' : '',
                    ].filter(Boolean).join(' ')}
                    key={`${graduate.key}-${index}`}
                    dir="rtl"
                    onClick={() => {
                      if (isPeek) goToVideoSlide(index);
                    }}
                    onKeyDown={(event) => {
                      if (isPeek && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        goToVideoSlide(index);
                      }
                    }}
                    role={isPeek ? 'button' : undefined}
                    tabIndex={isPeek ? 0 : undefined}
                  >
                    <div className="gl-stories-media">
                      <div className="video-testimonial-card gl-stories-media__card">
                        <GraduateVideoPlayer src={graduate.src} name={graduate.name} role={graduate.role} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            className="video-carousel-nav video-carousel-nav--next graduates-carousel-nav"
            onClick={() => goToVideoSlide(videoTrackIndex + 1)}
            aria-label="המלצה הבאה"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9.5 5.5 15 12l-5.5 6.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        </div>
      </div>

      <div className="reviews-showcase">
        <header className="reviews-head">
          <h2 className="section-title">{REVIEWS_TITLE}</h2>
        </header>
        <SnapCarousel
          items={WRITTEN_REVIEWS}
          className="feedback-carousel-shell"
          visibleCount={1}
          align="center"
          ariaPrev="ביקורת קודמת"
          ariaNext="ביקורת הבאה"
          renderItem={(item, index, isActive) => (
            <article
              className={`review-card${isActive ? ' is-active' : ''}`}
              key={item.key}
            >
              <span className="review-card-mark" aria-hidden="true">״</span>
              <div className="review-card-text">
                {item.paragraphs.filter(Boolean).map((paragraph, paragraphIndex) => (
                  <p key={`${item.key}-p-${paragraphIndex}`}>{paragraph}</p>
                ))}
              </div>
            </article>
          )}
        />
      </div>
    </>
  );
}
